import { useEffect, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { PageHeader } from '@components/PageHeader';
import {
  useGetExperimentsBySample,
  useSearchSampleID,
} from '@/api/sample/sample';
import { type SampleSearchResult } from '@/api/sample/types';

import SampleSearch from './Components/SampleSearch';
import SampleResults from './Components/SampleResults';

const SEARCH_DEBOUNCE_MS = 275;
const MINIMUM_SEARCH_LENGTH = 2;

const Samples = () => {
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedSample, setSelectedSample] =
    useState<SampleSearchResult | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchValue]);

  const normalizedSearch = searchValue.trim();
  const searchIsSettled = normalizedSearch === debouncedSearch;
  const searchIsValid =
    debouncedSearch.length === 0 ||
    debouncedSearch.length >= MINIMUM_SEARCH_LENGTH;
  const sampleQuery = useSearchSampleID(
    debouncedSearch,
    searchIsSettled && searchIsValid,
  );
  const experimentQuery = useGetExperimentsBySample(
    {
      id: selectedSample?.sample_id || '',
      name: '',
      date: '',
    },
    Boolean(selectedSample),
    selectedSample?.id,
  );

  const handleClearSearch = () => {
    setSearchValue('');
    setDebouncedSearch('');
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1600, mx: 'auto' }}>
      <PageHeader
        eyebrow="Laboratory operations"
        title="Samples"
        description="Find sample-linked experiments and review their analysis progress from one workspace."
      />
      <Stack spacing={3}>
        <SampleSearch
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          onClearSearch={handleClearSearch}
          samples={sampleQuery.data || []}
          selectedSampleId={selectedSample?.id}
          onSelectSample={setSelectedSample}
          isLoading={
            !searchIsSettled ||
            (searchIsValid && (sampleQuery.isPending || sampleQuery.isFetching))
          }
          isError={sampleQuery.isError}
          minimumSearchLength={MINIMUM_SEARCH_LENGTH}
          searchIsValid={searchIsValid}
          onRetry={() => sampleQuery.refetch()}
        />
        <SampleResults
          selectedSample={selectedSample}
          resultData={experimentQuery.data || []}
          searchCompleted={Boolean(selectedSample) && experimentQuery.isSuccess}
          isLoading={experimentQuery.isPending && Boolean(selectedSample)}
          isError={experimentQuery.isError}
          onRetry={() => experimentQuery.refetch()}
        />
      </Stack>
    </Box>
  );
};

export default Samples;
